import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Download, Search } from "lucide-react";

const STATUSES = ["pending", "confirmed", "shipped", "delivered", "cancelled"];

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState(""); // "" | "guest" | "registered"

  const refresh = () => api.get("/admin/orders").then((r) => setOrders(r.data));

  useEffect(() => {
    refresh();
    // `refresh` is a stable in-scope fetch helper; run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateStatus = async (id, status) => {
    await api.patch(`/admin/orders/${id}`, { status });
    toast.success("Status updated");
    refresh();
  };

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return orders.filter((o) => {
      if (statusFilter && o.status !== statusFilter) return false;
      if (typeFilter === "guest" && !o.is_guest && o.user_id) return false;
      if (typeFilter === "registered" && (o.is_guest || !o.user_id)) return false;
      if (!needle) return true;
      const hay = [
        o.id,
        o.user_email,
        o.shipping?.phone,
        o.shipping?.full_name,
        o.shipping?.city,
        o.coupon_code,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(needle);
    });
  }, [orders, q, statusFilter, typeFilter]);

  const exportCsv = () => {
    const rows = [
      [
        "order_id",
        "created_at",
        "status",
        "payment_method",
        "is_guest",
        "customer_email",
        "customer_name",
        "phone",
        "city",
        "address",
        "items_count",
        "subtotal",
        "discount",
        "total",
        "coupon_code",
      ],
      ...filtered.map((o) => [
        o.id,
        o.created_at || "",
        o.status,
        o.payment_method || "installation",
        o.is_guest ? "yes" : "no",
        o.user_email || "",
        o.shipping?.full_name || "",
        o.shipping?.phone || "",
        o.shipping?.city || "",
        (o.shipping?.address || "").replace(/[\r\n]+/g, " "),
        o.items?.length || 0,
        (o.subtotal || 0).toFixed(2),
        (o.discount || 0).toFixed(2),
        (o.total || 0).toFixed(2),
        o.coupon_code || "",
      ]),
    ];
    const csv = rows
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `orders-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="admin-orders">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-slate-500">Operations</div>
          <h1 className="mt-2 font-display font-extrabold text-4xl tracking-tight">Orders</h1>
          <div className="mt-1 text-sm text-slate-500 font-mono">
            {filtered.length} of {orders.length} shown
          </div>
        </div>
        <button
          type="button"
          onClick={exportCsv}
          data-testid="admin-orders-export"
          className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-ink text-white hover:bg-electric text-xs font-mono uppercase tracking-widest"
        >
          <Download size={14} /> Export CSV
        </button>
      </div>

      <div className="mt-6 grid sm:grid-cols-[1fr_200px_200px] gap-3">
        <label className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="admin-orders-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by id, email, phone, name, city…"
            className="w-full h-10 pl-9 pr-3 border border-black/15 focus:border-electric focus:outline-none text-sm"
          />
        </label>
        <select
          data-testid="admin-orders-filter-status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-10 px-3 border border-black/15 text-sm font-mono uppercase bg-white"
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select
          data-testid="admin-orders-filter-type"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="h-10 px-3 border border-black/15 text-sm font-mono uppercase bg-white"
        >
          <option value="">All customers</option>
          <option value="guest">Guests only</option>
          <option value="registered">Registered only</option>
        </select>
      </div>

      <div className="mt-6 border border-black/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-left">
            <tr>
              <th className="px-4 py-3 text-[10px] font-mono uppercase tracking-widest">Order</th>
              <th className="px-4 py-3 text-[10px] font-mono uppercase tracking-widest">Customer</th>
              <th className="px-4 py-3 text-[10px] font-mono uppercase tracking-widest">Phone</th>
              <th className="px-4 py-3 text-[10px] font-mono uppercase tracking-widest">Total</th>
              <th className="px-4 py-3 text-[10px] font-mono uppercase tracking-widest">Items</th>
              <th className="px-4 py-3 text-[10px] font-mono uppercase tracking-widest">Status</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((o) => (
              <React.Fragment key={o.id}>
                <tr className="border-t border-black/10">
                  <td className="px-4 py-3 font-mono">
                    #{o.id.slice(0, 8).toUpperCase()}
                    {o.is_guest && (
                      <span className="ml-2 text-[9px] font-mono uppercase tracking-widest bg-electric/10 text-electric px-1.5 py-0.5 rounded">
                        Guest
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="truncate max-w-[220px]">{o.user_email}</div>
                    {o.shipping?.full_name && (
                      <div className="text-[11px] text-slate-500 truncate max-w-[220px]">{o.shipping.full_name}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{o.shipping?.phone || "—"}</td>
                  <td className="px-4 py-3 font-mono">{o.total.toFixed(2)} JOD</td>
                  <td className="px-4 py-3 font-mono">{o.items.length}</td>
                  <td className="px-4 py-3">
                    <select
                      data-testid={`admin-order-status-${o.id}`}
                      value={o.status}
                      onChange={(e) => updateStatus(o.id, e.target.value)}
                      className="h-9 px-2 border border-black/15 text-xs font-mono uppercase bg-white"
                    >
                      {STATUSES.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => setExpanded(expanded === o.id ? null : o.id)}
                      className="text-xs font-mono uppercase tracking-widest hover:text-electric"
                    >
                      {expanded === o.id ? "Hide" : "View"}
                    </button>
                  </td>
                </tr>
                {expanded === o.id && (
                  <tr className="bg-secondary">
                    <td colSpan={7} className="px-4 py-4 text-sm">
                      <div className="grid sm:grid-cols-2 gap-6">
                        <div>
                          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">Items</div>
                          <ul className="space-y-1 font-mono">
                            {o.items.map((i, idx) => (
                              <li key={`${i.product_id || i.name}-${i.variant_id || i.variant_label || idx}`}>
                                {i.name}
                                {i.variant_label ? ` · ${i.variant_label}` : ""} ×{i.quantity} — {i.price.toFixed(2)} JOD
                              </li>
                            ))}
                          </ul>
                          <div className="mt-3 text-[11px] font-mono text-slate-500">
                            Payment: {o.payment_method || "installation"}
                            {o.coupon_code && <> · Coupon: {o.coupon_code}</>}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">Shipping</div>
                          <div className="font-mono">
                            {o.shipping?.full_name}
                            <br />
                            {o.shipping?.phone}
                            <br />
                            {o.shipping?.address}, {o.shipping?.city}
                            {o.shipping?.notes && (
                              <>
                                <br />
                                <span className="text-slate-500">Notes: {o.shipping.notes}</span>
                              </>
                            )}
                          </div>
                          <a
                            href={`https://wa.me/${(o.shipping?.phone || "").replace(/[^\d]/g, "")}?text=${encodeURIComponent(
                              `Hi ${o.shipping?.full_name || ""}, about your Shams Al Shareqa order #${o.id
                                .slice(0, 8)
                                .toUpperCase()} —`
                            )}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-3 inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest text-electric hover:underline"
                          >
                            Contact on WhatsApp →
                          </a>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="p-12 text-center text-slate-500 font-mono">
            {orders.length === 0 ? "No orders yet." : "No orders match your filters."}
          </div>
        )}
      </div>
    </div>
  );
}
